import { MdCheckCircle, MdWarning, MdInfo, MdError, MdNotificationsNone, MdDoneAll } from 'react-icons/md';
import { AiOutlineClose } from 'react-icons/ai';
import ModalPortal from '../lib/modalPortal';

const TYPE_CONFIG = {
    SUCCESS: { icon: MdCheckCircle, color: 'text-green-500',  bg: 'bg-green-50'  },
    WARNING: { icon: MdWarning,      color: 'text-yellow-500', bg: 'bg-yellow-50' },
    ERROR:   { icon: MdError,        color: 'text-red-500',    bg: 'bg-red-50'    },
    INFO:    { icon: MdInfo,         color: 'text-blue-500',   bg: 'bg-blue-50'   },
};

function relativeTime(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return 'agora';
    if (mins < 60)  return `${mins}min atrás`;
    if (hours < 24) return `${hours}h atrás`;
    return `${days}d atrás`;
}

function NotificationItem({ notification, userId, onMarkAsRead }) {
    const cfg = TYPE_CONFIG[notification.type] || TYPE_CONFIG.INFO;
    const Icon = cfg.icon;
    const isUnread = !notification.readByUsers?.includes(userId);

    return (
        <div
            className={`flex gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${isUnread ? 'bg-blue-50/40' : ''}`}
            onClick={() => isUnread && onMarkAsRead(notification.id)}
        >
            <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center`}>
                <Icon size={18} className={cfg.color} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-semibold text-gray-800 leading-snug ${isUnread ? 'font-bold' : ''}`}>
                        {notification.title}
                    </p>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                        {relativeTime(notification.createdAt)}
                    </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{notification.message}</p>
            </div>
            {isUnread && (
                <div className="self-center flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
            )}
        </div>
    );
}

export function NotificationDrawer({ isOpen, onClose, notifications, unreadCount, markAsRead, markAllAsRead }) {
    const userId = sessionStorage.getItem('userId');

    return (
        <ModalPortal>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={onClose}
                />
            )}

            {/* Drawer */}
            <div className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
                    <div className="flex items-center gap-2">
                        <h2 className="font-bold text-gray-800">Notificações</h2>
                        {unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
                                {unreadCount}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="flex items-center gap-1 text-xs text-custom_blue font-semibold hover:underline"
                                title="Marcar todas como lidas"
                            >
                                <MdDoneAll size={16} /> Marcar todas
                            </button>
                        )}
                        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
                            <AiOutlineClose size={20} />
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                    {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                            <MdNotificationsNone size={48} className="opacity-30" />
                            <p className="text-sm">Nenhuma notificação</p>
                        </div>
                    ) : (
                        notifications.map(n => (
                            <NotificationItem
                                key={n.id}
                                notification={n}
                                userId={userId}
                                onMarkAsRead={markAsRead}
                            />
                        ))
                    )}
                </div>
            </div>
        </ModalPortal>
    );
}
